import ForYouTabIcon from "assets/svgs/ForYouTabIcon";
import HighSalaryTabIcon from "assets/svgs/HighSalaryTabIcon";
import NearbyTabIcon from "assets/svgs/NearbyTabIcon";
import NewJobsTabIcon from "assets/svgs/NewJobsTabIcon";
import useJobFeedStore from "components/RevampFeedDetails/JobFeed/store";
import Image from "next/image";
import { useState } from "react";
import { DISCOVERY_TABS_LABELS } from "utils/constants";
import Mixpanel from "utils/Mixpanel";

const DESKTOP_TABS = [
	{
		color: "#0074E8",
		bgColor: "#EBF3FE",
		id: 1,
		label: "For You",
		icon: ForYouTabIcon.desktop,
		selectedIcon: ForYouTabIcon.desktopSelected,
		selectedIconLink:
			"https://cdn.apna.co/apna-learn/Icons/SVG%20Icons/jobs_for_you_selected.webp",
	},
	{
		color: "#00857C",
		bgColor: "#E7F9F9",
		id: 2,
		label: "High Salary",
		icon: HighSalaryTabIcon.desktop,
		selectedIcon: HighSalaryTabIcon.desktopSelected,
		selectedIconLink:
			"https://cdn.apna.co/apna-learn/Icons/SVG%20Icons/high_salary_selected.webp",
	},
	{
		color: "#DE3700",
		bgColor: "#FFF2EB",
		id: 3,
		label: "Nearby",
		icon: NearbyTabIcon.desktop,
		selectedIcon: NearbyTabIcon.desktopSelected,
		selectedIconLink:
			"https://cdn.apna.co/apna-learn/Icons/SVG%20Icons/nearby_selected.webp",
	},
	{
		color: "#722ED1",
		bgColor: "#F1EAFA",
		id: 4,
		label: "New Jobs",
		icon: NewJobsTabIcon.desktop,
		selectedIcon: NewJobsTabIcon.desktopSelected,
		selectedIconLink:
			"https://cdn.apna.co/apna-learn/Icons/SVG%20Icons/new_job_selected.webp",
	},
];

const MOBILE_TABS = [
	{
		color: "#0074E8",
		id: 1,
		label: "For You",
		icon: ForYouTabIcon.mobile,
		selectedIcon: ForYouTabIcon.mobileSelected,
		placeholderBg: "#EBF3FE",
		animatedSelectedIcon:
			"https://cdn.apna.co/apna-learn/Thumbnail/Animation/NewIcon-ezgif.com-video-to-webp-converter.webp",
	},
	{
		color: "#00857C",
		id: 2,
		label: "High Salary",
		icon: HighSalaryTabIcon.mobile,
		selectedIcon: HighSalaryTabIcon.mobileSelected,
		placeholderBg: "#E7F9F9",
		animatedSelectedIcon:
			"https://cdn.apna.co/apna-learn/Thumbnail/Animation/HS-ezgif.com-video-to-webp-converter.webp",
	},
	{
		color: "#DE3700",
		id: 3,
		label: "Nearby",
		icon: NearbyTabIcon.mobile,
		selectedIcon: NearbyTabIcon.mobileSelected,
		placeholderBg: "#FFF2EB",
		animatedSelectedIcon:
			"https://cdn.apna.co/apna-learn/Thumbnail/Animation/Hose-ezgif.com-video-to-webp-converter%20(1).webp",
	},
	{
		color: "#722ED1",
		id: 4,
		label: "New Jobs",
		icon: NewJobsTabIcon.mobile,
		selectedIcon: NewJobsTabIcon.mobileSelected,
		placeholderBg: "#F1EAFA",
		animatedSelectedIcon:
			"https://cdn.apna.co/apna-learn/Thumbnail/Animation/Job-ezgif.com-video-to-webp-converter%20(1).webp",
	},
];

const DiscoveryTabs = () => {
	const { discoveryTabs, setActiveDiscoveryTab } = useJobFeedStore();
	const [tabKeys, setTabKeys] = useState({});

	if (!discoveryTabs.showTabs) {
		return null;
	}

	const handleDiscoveryTabClick = (tabId) => {
		Mixpanel.track("Discovery Collection Clicked", {
			"Collection Name": DISCOVERY_TABS_LABELS[tabId],
		});

		if (discoveryTabs?.activeTabId !== tabId) {
			setActiveDiscoveryTab(tabId);
			setTabKeys((prevKeys) => ({
				...prevKeys,
				[tabId]: Date.now(), // Update key only when tab is selected
			}));
		}
	};

	return (
		<div className="mx-auto w-full max-w-[1040px]">
			{/* Desktop View */}
			<div className="hidden justify-center space-x-[16px] rounded-[40px] py-[24px] md:flex md:justify-start">
				{DESKTOP_TABS.map((tab) => (
					<button
						type="button"
						key={tab.id}
						onClick={() => handleDiscoveryTabClick(tab.id)}
						className="flex items-center gap-[4px] rounded-[40px] border border-transparent bg-white p-[4px] pr-[16px] hover:bg-gray-100"
						style={{
							borderColor:
								discoveryTabs?.activeTabId === tab.id
									? tab.color
									: "transparent",
							background:
								discoveryTabs?.activeTabId === tab.id
									? tab.bgColor
									: "white",
						}}
					>
						{discoveryTabs?.activeTabId === tab.id ? (
							<Image
								key={discoveryTabs?.activeTabId}
								width={40}
								height={40}
								src={tab.selectedIconLink}
								alt={tab.label}
							/>
						) : (
							tab.icon
						)}

						<span
							className="text-[16px] leading-[24px] transition-opacity duration-200"
							style={{
								color:
									discoveryTabs?.activeTabId === tab.id
										? tab.color
										: "inherit",
								fontWeight:
									discoveryTabs?.activeTabId === tab.id ? 600 : 400,
								opacity: discoveryTabs?.activeTabId === tab.id ? 1 : 0.8,
							}}
						>
							{tab.label}
						</span>
					</button>
				))}
			</div>

			{/* Mobile View */}
			<div className="relative flex justify-between rounded-lg border-b border-b-[#EBF3FE] px-[16px]  md:hidden">
				{MOBILE_TABS.map((tab) => (
					<button
						type="button"
						key={tab.id}
						onClick={() => handleDiscoveryTabClick(tab.id)}
						className="relative flex flex-1 flex-col items-center gap-[8px] px-2 py-1 transition-[2s]"
					>
						{discoveryTabs?.activeTabId === tab.id ? (
							<div className="relative h-[48px] w-[48px]">
								{/* Placeholder background with tab color to compensate cdn img load time */}
								<div
									className="absolute inset-0 flex items-center justify-center rounded-full"
									style={{
										backgroundColor: tab.placeholderBg,
									}}
								/>
								{/* Animated Image */}
								<Image
									key={tabKeys[tab.id] || tab.id}
									src={`${tab.animatedSelectedIcon}?t=${
										tabKeys[tab.id] || Date.now()
									}`}
									alt={tab.label}
									width={48}
									height={48}
									className="absolute inset-0"
									priority
								/>
							</div>
						) : (
							tab.icon
						)}
						<span
							className="mb-[6px] text-nowrap text-[12px] leading-[16px] text-[#5C5C5C]"
							style={{
								color: discoveryTabs?.activeTabId === tab.id && tab.color,
								fontWeight:
									discoveryTabs?.activeTabId === tab.id ? 600 : 400,
							}}
						>
							{tab.label}
						</span>
						{discoveryTabs?.activeTabId === tab.id && (
							<div
								className="absolute bottom-0 left-0 h-1 w-full rounded-t-[3px] rounded-tr-[4px] transition-all"
								style={{ background: tab.color }}
							/>
						)}
					</button>
				))}
			</div>
		</div>
	);
};

export default DiscoveryTabs;
